import {Col, Row} from 'antd';
import React, {CSSProperties} from 'react';
import {Formy} from '../../../src/Formy/Formy';
import {useFormyValue} from '../../../src/Formy/hooks';
import {
  AD1_AD2_SOIL,
  ALREADY_DAMAGED,
  CROP_ALREADY_PLANTED,
  FIRST_OR_SECOND_YEAR_HARVEST,
  MGA_ERROR_STYLE,
  MGA_OK_STYLE,
  MGA_WARNING_STYLE,
  OTHER_INSURANCE,
  PLANTING_SYSTEM,
  PREVIOUS_CULTURE,
  SAME_CULTURE_ENTIRE_AREA,
  TOTAL_AREA_INSURED,
  ZONING_RECOMMENDATIONS,
} from '../../../src/custom/mga';
import {I18nFunction} from '../../../src/i18n/i18n';
import {AgriculturaMensagen, AgriculturaSendPropostaResponse} from '../../../src/models/agricultura';
import {RequestStatus} from '../../../src/models/interfaces';
import {formatDateTimeToLocale} from '../../../src/text/date';
import {useAsyncMemo} from '../../../src/util/hooks';
import {formatNumber} from '../../../src/util/number-util';
import {calculateSubsidy} from '../../../src/util/policy-calculations';
import {getPostgrestQueryParams} from '../../../src/util/postgrest-query';
import {FormyBool} from '../Formy/FormyBool';
import {FormyEnum} from '../Formy/FormyEnum';
import {Label} from '../Formy/FormyLabel';
import {FormyTextNum, FormyTextStr} from '../Formy/FormyText';
import {useApis} from '../apis/ApisContext';
import {PolicyForm} from './AddOrEditPolicy';

const STATUS_COLORS: Partial<Record<RequestStatus, React.CSSProperties>> = {
  new: MGA_WARNING_STYLE,
  ok: MGA_OK_STYLE,
  error: MGA_ERROR_STYLE,
};

const messageStyle = {whiteSpace: 'pre-wrap'};

function formatStatus(t: I18nFunction, status: string): string {
  if (status === 'new') {
    return t('Pending');
  } else if (status === 'error') {
    return t('Error');
  } else {
    return t('Submitted');
  }
}

// Note that the component is visible only to Brazil customers so we can hardcode the labels.
const formatMessage = (message: AgriculturaMensagen) => [
  `Codigo: ${message.codigo}`,
  `Descricao: ${message.descricao}`,
  `Tipo: ${message.tipo}`,
];

function formatResponse(
  response:
    | null
    | undefined
    | {
        result?: AgriculturaSendPropostaResponse;
        messages?: string[];
      },
) {
  const result: string[] = response?.messages ?? [];
  const mensagens = response?.result?.transacao.listaMensagens?.mensagem;
  if (Array.isArray(mensagens)) {
    result.push(...mensagens.flatMap(formatMessage));
  } else if (mensagens) {
    result.push(...formatMessage(mensagens));
  }
  return result;
}

const flexDirectionColumnSpaceBetween: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
};
export const AddOrEditPolicyBrazil: React.FC<{
  formy: Formy<PolicyForm>;
  totalPremium: number;
}> = ({formy, totalPremium}) => {
  const apis = useApis();
  const {t, locale} = apis;
  const policyId = useFormyValue(formy, 'policy_id');
  const federalSubsidyRequest = useAsyncMemo(async () => {
    if (!policyId) {
      return null;
    }
    const result = await apis.authedFetcher({
      method: 'GET',
      path: 'api/request',
      params: getPostgrestQueryParams({
        and: [
          {column: 'request->>policy_id', operator: 'eq', value: policyId},
          {column: 'request_type', operator: 'eq', value: 'brazil-subsidy-creation'},
        ],
      }),
    });
    if (!result.length) {
      return null;
    }
    return {
      formattedStatus: formatStatus(t, result[0].status),
      status: result[0].status as RequestStatus,
      response: formatResponse(result[0].response).join('\n'),
      updatedAtStr: formatDateTimeToLocale(result[0].updated_at, apis.locale),
    };
  }, [apis, t, policyId]);
  const customColumns = formy.getSectionFormy('custom_columns');
  const mgaCustomColumns = customColumns.getSectionFormy('mga');
  const federalSubsidyId = useFormyValue(mgaCustomColumns, 'federal_subsidy_id');
  const federalSubsidyPct = useFormyValue(mgaCustomColumns, 'federal_subsidy_percent');
  const stateSubsidyPct = useFormyValue(mgaCustomColumns, 'state_subsidy_percent');
  return (
    <>
      <Row gutter={16}>
        <h3>{t('AdditionalInfo')}</h3>
      </Row>
      <Row gutter={16}>
        <Col span={6} style={flexDirectionColumnSpaceBetween}>
          <Label>{t('ExternalPolicyId')}:</Label>
          <FormyTextStr formy={mgaCustomColumns} field={'axa_policy_id'} disabled={true} />
        </Col>
        <Col span={6} style={flexDirectionColumnSpaceBetween}>
          <Label>{t('NumberOfInstallments')}:</Label>
          <FormyTextNum formy={mgaCustomColumns} field={'installment_count'} min={1} max={10} />
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={6} style={flexDirectionColumnSpaceBetween}>
          <Label>{t('ExternalProposalId')}:</Label>
          <FormyTextStr formy={mgaCustomColumns} field={'ecampo_proposal_id'} disabled={true} />
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={6} style={flexDirectionColumnSpaceBetween}>
          <Label>{PREVIOUS_CULTURE}</Label>
          <FormyTextStr formy={mgaCustomColumns} field={'previous_crop'} />
        </Col>
        <Col span={6} style={flexDirectionColumnSpaceBetween}>
          <Label>{PLANTING_SYSTEM}</Label>
          <FormyEnum
            formy={mgaCustomColumns}
            field={'planting_type'}
            selectMsg={'Select'}
            options={[
              ['direct', 'direto'],
              ['conventional', 'convencional'],
            ]}
          />
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={6} style={flexDirectionColumnSpaceBetween}>
          <Label>{CROP_ALREADY_PLANTED}</Label>
          <FormyBool formy={mgaCustomColumns} field={'crop_already_planted'} selectMsg={null} />
        </Col>
        <Col span={6} style={flexDirectionColumnSpaceBetween}>
          <Label>{ZONING_RECOMMENDATIONS}</Label>
          <FormyBool formy={mgaCustomColumns} field={'aware_of_zoagro_mapa'} selectMsg={null} />
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={6} style={flexDirectionColumnSpaceBetween}>
          <Label>{SAME_CULTURE_ENTIRE_AREA}</Label>
          <FormyBool formy={mgaCustomColumns} field={'same_crop_whole_area'} selectMsg={null} />
        </Col>
        <Col span={6} style={flexDirectionColumnSpaceBetween}>
          <Label>{OTHER_INSURANCE}</Label>
          <FormyBool formy={mgaCustomColumns} field={'preexisting_policy_for_same_area'} selectMsg={null} />
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={6} style={flexDirectionColumnSpaceBetween}>
          <Label>{TOTAL_AREA_INSURED}</Label>
          <FormyBool formy={mgaCustomColumns} field={'total_planted_area_insured'} selectMsg={null} />
        </Col>
        <Col span={6} style={flexDirectionColumnSpaceBetween}>
          <Label>{FIRST_OR_SECOND_YEAR_HARVEST}</Label>
          <FormyBool formy={mgaCustomColumns} field={'first_or_second_year'} selectMsg={null} />
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={6} style={flexDirectionColumnSpaceBetween}>
          <Label>{ALREADY_DAMAGED}</Label>
          <FormyBool formy={mgaCustomColumns} field={'crop_already_damaged'} selectMsg={null} />
        </Col>
        <Col span={6} style={flexDirectionColumnSpaceBetween}>
          <Label>{AD1_AD2_SOIL}</Label>
          <FormyBool formy={mgaCustomColumns} field={'predominantly_ad1_ad2_soil'} selectMsg={null} />
        </Col>
      </Row>
      {federalSubsidyRequest && (
        <>
          <h3>{t('FederalSubsidy')}</h3>
          {federalSubsidyPct && federalSubsidyPct > 0 ? (
            <>
              <Row gutter={16}>
                <Col span={4}>
                  <Label>{t('Status')}:</Label>
                  <div style={STATUS_COLORS[federalSubsidyRequest.status]}>{federalSubsidyRequest.formattedStatus}</div>
                </Col>
                {federalSubsidyRequest.response && (
                  <Col span={12}>
                    <Label>{t('Message')}:</Label>
                    <div style={messageStyle}>{federalSubsidyRequest.response}</div>
                  </Col>
                )}
              </Row>
              <Row gutter={16}>
                <Col span={4}>
                  <Label>{t('Date')}:</Label>
                  <div>{federalSubsidyRequest.updatedAtStr}</div>
                </Col>
                {federalSubsidyId && (
                  <Col span={4}>
                    <Label>{t('ID')}:</Label>
                    <div>{federalSubsidyId}</div>
                  </Col>
                )}
              </Row>
            </>
          ) : null}
          <Row gutter={16}>
            <Col span={6}>
              <Label>{t('Percent')}:</Label>
              <FormyTextNum formy={mgaCustomColumns} field={'federal_subsidy_percent'} />
            </Col>
            {federalSubsidyPct ? (
              <Col span={6}>
                <Label>{t('Amount')}:</Label>
                <div>{formatNumber(locale, calculateSubsidy(totalPremium, federalSubsidyPct), true, false)}</div>
              </Col>
            ) : null}
          </Row>
        </>
      )}
      <>
        <h3>{t('StateSubsidy')}</h3>
        <Row gutter={16}>
          <Col span={6}>
            <Label>{t('Percent')}:</Label>
            <FormyTextNum formy={mgaCustomColumns} field={'state_subsidy_percent'} />
          </Col>
          {stateSubsidyPct ? (
            <Col span={6}>
              <Label>{t('Amount')}:</Label>
              <div>{formatNumber(locale, calculateSubsidy(totalPremium, stateSubsidyPct), true, false)}</div>
            </Col>
          ) : null}
        </Row>
      </>
    </>
  );
};
